import { CSSProperties, memo, useEffect } from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';

import styled, { css, isPropValid } from '@mint-lib/styled';

import {
  BUILDER_ROW_TYPES,
  DRAG_ROW_TYPE,
  ROW_HEIGHT,
} from '../../../../../constants/campaignBuilder.js';
import { isDeprecatedPlatform } from '../../../../../constants/deprecatedPlatforms.js';
import { dragItemTypes } from '../../../../../constants/myCampaigns.js';
import { TRACKING_EVENTS } from '../../../../../types/performanceTracking.js';
import { BuilderTableRow, DraggableItemType } from '../../../types.js';
import AddRowButton from './AddRowButton.jsx';
import TableBodyItem from './TableBodyItem.jsx';
import { getPlatformIdentifier } from './utils.js';

const HIGHLIGHT_TIMEOUT = 2000;

type Props = {
  row: BuilderTableRow;
  style: CSSProperties;
  onDrop: (data: {
    item: DraggableItemType;
    row: BuilderTableRow;
    type?: string;
  }) => void;
  setRowState?: (id: number[], { isNewAdded }: { isNewAdded: boolean }) => void;
  isDisabled?: boolean;
  isParentHighlighted?: boolean;
  startMeasurePerformance: (event: TRACKING_EVENTS) => void;
};

const TableRow = memo(
  ({
    row,
    onDrop,
    style,
    isDisabled,
    setRowState,
    isParentHighlighted,
    startMeasurePerformance,
  }: Props) => {
    const {
      id: rowId,
      parent,
      type,
      scale,
      order,
      isPredictivePlanningMode,
      isEditMode,
      isArchivedVersion,
    } = row.original;
    const isTotal = type === BUILDER_ROW_TYPES.TOTAL;

    const platformIdentifier = getPlatformIdentifier(row);

    const isDeprecated = isDeprecatedPlatform(platformIdentifier);

    const showAddRowButton =
      row.original.type !== BUILDER_ROW_TYPES.CHANNEL &&
      !isTotal &&
      isEditMode &&
      !isArchivedVersion &&
      !isDeprecated;

    const handleDrop = (item: DraggableItemType) => {
      if (item.rowType) {
        startMeasurePerformance(TRACKING_EVENTS.ROW_ADDITION);
      }

      onDrop({ item, row, type: item.rowType });
    };

    // @ts-ignore
    const [{ isOver, item, canDrop }, drop] = useDrop<
      any,
      any,
      { isOver: boolean; item: any; canDrop: boolean }
    >({
      accept: [dragItemTypes.ADD_ROW, DRAG_ROW_TYPE, dragItemTypes.ADD_TILE],
      drop: handleDrop,
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        item: monitor.getItem(),
        canDrop: monitor.canDrop(),
      }),
      //@ts-ignore
      canDrop: (
        { id, original, name: itemName }: DraggableItemType,
        monitor: DropTargetMonitor & {
          internalMonitor: { getTargetIds: () => string[] };
        },
      ) => {
        if (isDeprecated) {
          return false;
        }

        const isReorder = original?.parent === parent && id !== row.id;

        const isAddRow =
          itemName === DRAG_ROW_TYPE &&
          (type === BUILDER_ROW_TYPES.PLATFORM ||
            type === BUILDER_ROW_TYPES.MEDIA_NETWORK ||
            type === BUILDER_ROW_TYPES.TILE);

        const isOneTarget = monitor.internalMonitor.getTargetIds().length === 1;

        return isOneTarget && isOver && (isReorder || isAddRow);
      },
    });

    useEffect(() => {
      if (!row.state?.isNewAdded) {
        return;
      }
      const timerId = setTimeout(() => {
        row.setState({ isNewAdded: false });
      }, HIGHLIGHT_TIMEOUT);

      return () => clearTimeout(timerId);
    }, [row]);

    return (
      <>
        <Tr
          {...row.getRowProps({
            style,
          })}
          ref={drop}
          isDragging={item?.id && item?.id === rowId}
          $isTotal={isTotal}
          $isHighlighted={row.state?.isNewAdded || isParentHighlighted}
          $isDisabled={isDisabled}
          $isPredictive={isPredictivePlanningMode}
          className="tr"
        >
          {row.cells.map((cell) => (
            <TableBodyItem
              cell={cell}
              row={row}
              key={`${cell.column.id}${row.id}`}
            />
          ))}
          {showAddRowButton ? (
            <AddRowButton
              row={row}
              startMeasurePerformance={startMeasurePerformance}
              setRowState={setRowState}
            />
          ) : null}
        </Tr>

        <DropIconWrapper
          style={style}
          $isHidden={!canDrop || !isOver}
          $topBorder={(order || 0) < item?.original?.order}
          $scale={scale}
          $margin={ROW_HEIGHT}
        >
          <Container $width={row.cells[0]?.column?.width * scale}>
            <IconContainer $scale={scale}>
              <Line $scale={scale} />
              {/* <AddIcon /> */}
            </IconContainer>
          </Container>
        </DropIconWrapper>
      </>
    );
  },
);

const Tr = styled('div')<{
  isDragging: boolean;
  $topBorder: boolean;
  $isHidden: boolean;
  $isTotal: boolean;
  $isHighlighted: boolean;
  $isDisabled: boolean;
  $isPredictive: boolean;
}>`
  text-transform: capitalize;
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      pointer-events: none !important;
    `}

  td {
    ${({ $isHighlighted, theme }) =>
      $isHighlighted &&
      css`
        background-color: ${theme.palette.background.success};
      `}
  }

  &:hover .MuiTableCell-root {
    background-color: ${({ theme, $isTotal, $isPredictive }) =>
      $isTotal && $isPredictive
        ? theme.palette.background.primary
        : theme.palette.ui.hoverRow};
  }

  &:hover .icon-draggable {
    visibility: visible;
  }

  &:hover .MuiTableCell-root:last-of-type {
    opacity: 1;
    visibility: visible;
  }
`;

const DropIconWrapper = styled('div', { shouldForwardProp: isPropValid })<{
  $isHidden: boolean;
  $scale: number;
  $margin: number;
  $topBorder: boolean;
}>`
  z-index: 2;
  ${({ $scale }) => css`
    height: ${16 * $scale}px !important;
  `}

  ${({ $isHidden, $topBorder, $margin, $scale }) => {
    if ($isHidden) {
      return 'display:none;';
    }
    return $topBorder
      ? css`
          margin-bottom: ${$margin * $scale}px;
        `
      : css`
          margin-top: ${$margin * $scale}px;
        `;
  }}
`;

const Container = styled('div', { shouldForwardProp: isPropValid })<{
  $width: number;
}>`
  position: sticky;
  left: 1px;
  z-index: 2;
  width: ${({ $width }) => $width - 1}px;
`;

const IconContainer = styled('div', { shouldForwardProp: isPropValid })<{
  $scale: number;
}>`
  ${({ $scale }) => css`
    top: ${-10.5 * $scale}px;
    height: ${16 * $scale}px;

    svg {
      width: ${20 * $scale}px;
      height: ${20 * $scale}px;
      display: flex;
    }
  `}
  width: 100%;
  left: 0;
  position: absolute;
`;

const Line = styled('div', { shouldForwardProp: isPropValid })<{
  $scale: number;
}>`
  ${({ $scale }) => css`
    height: ${2 * $scale}px;
    top: ${9.5 * $scale}px;
  `}
  width: inherit;
  border-radius: 3px;
  background-color: ${({ theme }) => theme.palette.action.focusBorder};
  position: absolute;
`;

export default TableRow;
